<script setup>
/**
 * OdpReports.vue
 * Core reporting layout dashboard supporting reactive query configurations,
 * dynamic data table sorting, asynchronous lifecycle hooks, and cross-origin iframe print triggers.
 */
import {ref, computed, onMounted} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import moment from 'moment'
import * as CSV from 'csv-string'
import {useOdpReportsStore} from '@/stores/odpReports'
import {useModalsStore} from '@/stores/modals'
import PrintReport from './PrintReport.vue'
import ReportMenu from './ReportMenu.vue'
import MonthsFilter from "@/bespoke/odp/report/MonthsFilter.vue"
import FranchisesFilter from "@/bespoke/odp/report/FranchisesFilter.vue"
import DateRangeFilter from "./filters/DateRangeFilter.vue"
import SchoolsFilter from "@/bespoke/odp/report/SchoolsFilter.vue"
import MonthInTheLastFilter from "@/bespoke/odp/report/MonthInTheLastFilter.vue"
import Button from "@/common/Button.vue"

// Vue Router Composables
const $route = useRoute()
const $router = useRouter()

// Pinia Store Instances
const odpReportsStore = useOdpReportsStore()
const modalsStore = useModalsStore()

// Component Template Reference Links
const container = ref(null)
const form = ref(null)
const toPrint = ref(null)

// Local Reactive State Definitions
const error = ref('')
const sortBy = ref('')
const sortDir = ref('asc')

// Computed Intermediaries derived directly from Pinia store instance
const formData = computed(() => odpReportsStore.formData)
const isLoading = computed(() => odpReportsStore.isLoading)
const franchises = computed(() => odpReportsStore.franchises)
const rows = computed(() => odpReportsStore.rows)
const startDate = computed(() => odpReportsStore.formData.startDate)
const endDate = computed(() => odpReportsStore.formData.endDate)

const reportLabel = computed(() => {
    const activeReport = odpReportsStore.reports.find(o => o.name === formData.value.reportName)
    return activeReport ? activeReport.label : ''
})

const sortedRows = computed(() => {
    if (!rows.value) return []

    // Create a shadow duplicate array instance to enforce mutation isolation standards
    return [...rows.value].sort((a, b) => {
        // todo: Refactor payload architecture to export formal field metadata arrays
        // to avoid string key evaluation hacks for decimal casting.
        if (sortBy.value === 'Credit amount') {
            return sortDir.value === 'desc'
                ? b[sortBy.value] - a[sortBy.value]
                : a[sortBy.value] - b[sortBy.value]
        } else {
            if (sortDir.value === 'desc') {
                return b[sortBy.value] > a[sortBy.value] ? 1 : -1
            } else {
                return a[sortBy.value] > b[sortBy.value] ? 1 : -1
            }
        }
    })
})

// Lifecycle Initialization Hooks
onMounted(() => {
    odpReportsStore.init({
        query: $route.query
    })
})

// Component Functional Core Routines
function setSortBy(heading) {
    sortDir.value = sortBy.value === heading
        ? (sortDir.value === 'asc' ? 'desc' : 'asc')
        : 'asc'

    sortBy.value = heading
}

function submitForm() {
    error.value = ''

    if (form.value && !form.value.checkValidity()) {
        Array.from(form.value.elements).forEach((element) => {
            if (element.validity.valid === false && error.value === '') {
                error.value += element.validationMessage
            }
        })
        return
    }

    // Apply active payload parameters back onto the query layer
    $router.push({query: formData.value})
    odpReportsStore.loadRows()
}

function openPrintModal() {
    modalsStore.show({
        componentName: 'PrintReportModal',
        obj: {html: toPrint.value.$el.innerHTML}
    })
}

function downloadCSVData() {
    if (!rows.value || !rows.value.length) return

    // Extract CSV Header Matrix Elements
    let headings = Object.keys(rows.value[0])
    let csv = CSV.stringify(headings)

    // Build Payload Value Vectors
    rows.value.forEach((row) => {
        let values = Object.values(row)
        csv += CSV.stringify(values)
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = `${reportLabel.value} ${getDate(new Date())}.csv`
    anchor.click()
}

function getDate(timestamp) {
    return moment(timestamp).format('DD-MM-Y')
}

function formatNumber(value) {
    return new Intl.NumberFormat().format(value)
}
</script>

<template>
    <div>
        <div v-if="franchises.length" ref="container">
            <nav class="navbar navbar-default">
                <div class="container-fluid">
                    <form ref="form" class="float-start d-flex align-items-center gap-1">
                        <ReportMenu :reports-module="odpReportsStore"/>

                        <template v-if="formData.reportName === 'birthdayDates'">
                            <MonthsFilter/>
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'birthdayPartyEnquiries'">
                            <FranchisesFilter :allow-all="true"/>
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                        </template>

                        <template v-if="formData.reportName === 'bookingsReport'">
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'courseDatesData'">
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'customerBookingData'">
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'customerCredit'">
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'customerPostcodes'">
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'marketingReport'">
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                        </template>

                        <template v-if="formData.reportName === 'postcodes'">
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'partyBookings'">
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                            <FranchisesFilter :allow-all="true"/>
                        </template>

                        <template v-if="formData.reportName === 'referAFriendLTV'">
                            <FranchisesFilter :allow-all="true"/>
                            <DateRangeFilter :reports-module="odpReportsStore"/>
                        </template>

                        <template v-if="formData.reportName === 'schoolParents'">
                            <SchoolsFilter/>
                            <span class="text-nowrap text-muted">in the last</span>
                            <MonthInTheLastFilter/>
                        </template>

                        <button v-if="formData.reportName !== ''" class="btn btn-primary btn-sm"
                                @click.prevent="submitForm()">Go
                        </button>

                        <span v-if="rows && rows.length" class="text-nowrap">
                            {{ formatNumber(rows.length) }} records found
                        </span>

                        <span v-if="isLoading" class="ajax-spinner">
                            <span class="ajax-spinner-icon ajax-spinner-icon-sm"/>
                        </span>
                    </form>

                    <div v-if="rows && rows.length" class="float-end">
                        <Button @click="openPrintModal">
                            <i class="bi bi-printer"/>
                            <span class="visually-hidden">Print report</span>
                        </Button>

                        <Button @click.prevent="downloadCSVData">
                            <i class="bi bi-download"/>
                            <span class="visually-hidden">Download CSV</span>
                        </Button>
                    </div>

                </div>
            </nav>

            <p v-if="rows && rows.length === 0" class="mx-2 alert alert-warning">
                No records match your search.
            </p>

            <p v-if="error" class="mx-2 alert alert-warning" v-html="error"></p>

            <div v-if="rows && rows.length" class="padding-sm table-wrapper">
                <table class="table table-condensed table-striped">
                    <thead>
                    <tr>
                        <td v-for="(value, heading) in rows[0]" :key="'heading-' + heading" @click="setSortBy(heading)">
                            <span v-if="heading === sortBy"
                                  :class="{ 'bi-chevron-up': sortDir === 'asc', 'bi-chevron-down': sortDir === 'desc' }"/>
                            {{ heading }}
                        </td>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="(row, index) in sortedRows" :key="'row-' + index">
                        <td v-for="(value, heading) in row" :key="'cell-' + heading" v-html="value"></td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <PrintReport v-if="rows" ref="toPrint" :end-date="endDate" :heading="reportLabel" :rows="sortedRows"
                     :start-date="startDate" style="display: none"/>
    </div>
</template>

<style scoped>
.ajax-spinner {
    vertical-align: bottom;
}
</style>